/**
 * Saved Card Selector Component
 * Shows radio button list of user's saved cards for selection during checkout
 */

import { useState } from 'react'
import type { PaymentMethod } from '../../types/paymentMethod'

interface SavedCardSelectorProps {
  paymentMethods: PaymentMethod[]
  selectedId: string | null
  onSelect: (paymentMethodId: string) => void
  onAddNew: () => void
  onManageCards?: () => void
}

const CARD_BRAND_ICONS: Record<string, string> = {
  visa: '💳',
  mastercard: '💳',
  amex: '💳',
  discover: '💳',
  diners: '💳',
  jcb: '💳',
  unionpay: '💳'
}

export default function SavedCardSelector({
  paymentMethods,
  selectedId,
  onSelect,
  onAddNew,
  onManageCards
}: SavedCardSelectorProps) {
  const [showAll, setShowAll] = useState(false)

  const defaultCard = paymentMethods.find(pm => pm.is_default)
  const visibleCards = showAll ? paymentMethods : (defaultCard ? [defaultCard] : paymentMethods.slice(0, 1))

  const formatExpiration = (month: number, year: number) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`
  }

  const isExpired = (month: number, year: number) => {
    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1
    return year < currentYear || (year === currentYear && month < currentMonth)
  }

  const getBrandIcon = (brand: string) => {
    return CARD_BRAND_ICONS[brand.toLowerCase()] || '💳'
  }

  const capitalizeFirst = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  return (
    <div className="space-y-3">
      {/* Saved cards list */}
      <div className="space-y-2">
        {visibleCards.map((pm) => {
          const expired = isExpired(pm.exp_month, pm.exp_year)

          return (
            <label
              key={pm.id}
              className={`
                flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors
                ${selectedId === pm.id
                  ? 'border-[#5BB4D6] bg-[#F0F8FF]'
                  : 'border-gray-200 hover:border-[#B8E2F8] hover:bg-gray-50'
                }
                ${expired ? 'opacity-60' : ''}
              `}
            >
              <input
                type="radio"
                name="payment_method"
                value={pm.id}
                checked={selectedId === pm.id}
                onChange={() => onSelect(pm.id)}
                disabled={expired}
                className="w-4 h-4 text-[#4AA3CC] focus:ring-[#4AA3CC]"
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getBrandIcon(pm.brand)}</span>
                  <span className="font-medium text-gray-900">
                    {capitalizeFirst(pm.brand)} •••• {pm.last4}
                  </span>
                  {pm.is_default && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-[#D4EEFF] text-[#2C7799] rounded">
                      Default
                    </span>
                  )}
                  {expired && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                      Expired
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  Expires {formatExpiration(pm.exp_month, pm.exp_year)}
                </div>
              </div>
            </label>
          )
        })}
      </div>

      {/* Show more/less cards */}
      {paymentMethods.length > 1 && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="text-sm text-[#3B8DB3] hover:text-[#2C7799] font-medium"
        >
          Show {paymentMethods.length - visibleCards.length} more card{paymentMethods.length - visibleCards.length > 1 ? 's' : ''}
        </button>
      )}

      {/* Add new card option */}
      <label
        className={`
          flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors
          ${selectedId === 'new'
            ? 'border-[#5BB4D6] bg-[#F0F8FF]'
            : 'border-gray-200 hover:border-[#B8E2F8] hover:bg-gray-50'
          }
        `}
      >
        <input
          type="radio"
          name="payment_method"
          value="new"
          checked={selectedId === 'new'}
          onChange={() => onAddNew()}
          className="w-4 h-4 text-[#4AA3CC] focus:ring-[#4AA3CC]"
        />
        <div className="flex-1">
          <span className="font-medium text-gray-900">+ Add New</span>
        </div>
      </label>

      {/* Manage cards link */}
      {onManageCards && paymentMethods.length > 0 && (
        <button
          onClick={onManageCards}
          className="text-sm text-gray-600 hover:text-gray-900 underline"
        >
          Manage saved cards
        </button>
      )}
    </div>
  )
}
